import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useCallback } from 'react';
import { queryKeys } from '../../../config/queryClient';
import { getTaskDetails } from 'services/CaseServices';

/**
 * Hook to fetch task details using React Query
 *
 * @param {string} taskId - Task ID to fetch
 * @param {Object} options - Query options
 * @param {boolean} options.enabled - Enable/disable query (default: true)
 * @param {Function} options.onSuccess - Callback on success
 * @param {Function} options.onError - Callback on error
 *
 * @returns {Object} - task data, loading state, error, refetch
 */
export const useTaskDetailQuery = (taskId, options = {}) => {
  const { enabled = true, onSuccess, onError } = options;

  const query = useQuery({
    queryKey: [...queryKeys.tasks.all, 'detail', taskId],
    queryFn: async () => {
      const response = await getTaskDetails(taskId, '', true);
      return response.data;
    },
    enabled: Boolean(taskId) && enabled,
    staleTime: 1 * 60 * 1000, // Consider data stale after 1 minute
    gcTime: 5 * 60 * 1000, // Keep in cache for 5 minutes
    refetchOnWindowFocus: false,
    retry: 2,
    onSuccess,
    onError,
  });

  return {
    task: query.data,
    isLoading: query.isLoading,
    isFetching: query.isFetching,
    isError: query.isError,
    error: query.error,
    refetch: query.refetch,
    isSuccess: query.isSuccess,
  };
};

/**
 * Hook to prefetch task details
 * Useful for optimistic loading when hovering over task cards
 */
export const usePrefetchTaskDetail = () => {
  const queryClient = useQueryClient();

  return useCallback(
    (taskId) => {
      if (!taskId) return;

      queryClient.prefetchQuery({
        queryKey: [...queryKeys.tasks.all, 'detail', taskId],
        queryFn: async () => {
          const response = await getTaskDetails(taskId);
          return response.data;
        },
        staleTime: 1 * 60 * 1000,
      });
    },
    [queryClient]
  );
};
